import React, { useMemo } from 'react';
import logoUrl from '../../../assets/images/logo.svg';
import styles from './BranchesSection.module.css';

/**
 * Saudi Arabia as a dot matrix, with every branch pinned where it is and a
 * dashed arc from the Riyadh head office to each one. The head office always
 * carries a Sahari logo tag; a branch shows its own tag while it is pointed
 * at. Drawn in code (no map image or map service): a simplified border, a
 * grid of dots kept only where they fall inside it, and the branch points
 * projected from lat/lng.
 */

/** Branch locations (longitude, latitude), by the city names in contact.ts. */
export const BRANCH_COORDS: Record<string, [number, number]> = {
  RIYADH: [46.72, 24.71],
  JEDDAH: [39.17, 21.54],
  MAKKAH: [39.83, 21.42],
  MADINAH: [39.61, 24.47],
  TAIF: [40.42, 21.27],
  YANBU: [38.06, 24.09],
  RABIGH: [39.03, 22.8],
  DUBA: [35.7, 27.35],
  ABHA: [42.51, 18.22],
  'KHAMIS MUSHAIT': [42.73, 18.31],
  MUHAYIL: [42.05, 18.55],
  JIZAN: [42.55, 16.89],
};

export const HQ_CITY = 'RIYADH';

// Simplified Saudi border (lon, lat), clockwise from the Gulf of Aqaba.
const BORDER: [number, number][] = [
  [34.95, 29.35], [36.07, 29.19], [36.5, 29.5], [37.5, 30.0], [38.0, 30.5], [37.0, 31.5],
  [39.2, 32.15], [40.4, 31.9], [42.0, 31.1], [44.7, 29.2], [46.4, 29.1], [47.4, 29.0],
  [47.7, 28.5], [48.4, 28.55], [48.8, 27.7], [49.3, 27.2], [50.1, 26.6], [50.2, 25.8],
  [50.6, 25.0], [50.8, 24.75], [51.6, 24.25], [52.6, 22.94], [55.1, 22.6], [55.67, 22.0],
  [55.0, 20.0], [52.0, 19.0], [49.1, 18.6], [48.2, 18.2], [47.5, 17.5], [46.4, 17.2],
  [45.4, 17.3], [44.1, 17.4], [43.4, 17.5], [43.1, 16.9], [42.8, 16.4], [42.6, 16.8],
  [42.3, 17.4], [41.8, 17.9], [41.2, 18.7], [40.6, 19.8], [39.7, 20.9], [39.1, 21.5],
  [39.0, 22.5], [38.4, 23.6], [37.5, 24.3], [37.2, 24.9], [36.6, 25.8], [36.2, 26.6],
  [35.6, 27.4], [35.1, 28.1], [34.6, 28.1],
];

const K = 30;
const x = (lon: number) => (lon - 34.6) * K;
const y = (lat: number) => (32.15 - lat) * K * 1.05;
const W = x(55.67);
const H = y(16.35);
const STEP = 11;

const LOGO_RATIO = 48 / 263; // logo.svg height / width

/** White tag: the Sahari logo, a divider, then the place. */
const Tag: React.FC<{ x: number; y: number; hq: boolean; text: string }> = ({ x: px, y: py, hq, text }) => {
  const tw = hq ? 236 : 204;
  const th = hq ? 44 : 34;
  const lw = hq ? 118 : 92;
  const lh = lw * LOGO_RATIO;
  const tx = hq ? px + 22 : px + 14;
  const ty = hq ? py - th / 2 : py - th - 10;
  return (
    <g className={styles.tag}>
      <rect className={styles.tagBox} x={tx} y={ty} width={tw} height={th} rx={9} />
      <image href={logoUrl} x={tx + 10} y={ty + (th - lh) / 2} width={lw} height={lh} />
      <line className={styles.tagSep} x1={tx + lw + 18} y1={ty + 7} x2={tx + lw + 18} y2={ty + th - 7} />
      <text className={styles.tagCity} x={tx + lw + 27} y={ty + th / 2 + 4.5}>
        {text}
      </text>
    </g>
  );
};

function inside(px: number, py: number, poly: [number, number][]) {
  let c = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [ax, ay] = poly[i];
    const [bx, by] = poly[j];
    if ((ay > py) !== (by > py) && px < ((bx - ax) * (py - ay)) / (by - ay) + ax) c = !c;
  }
  return c;
}

const BranchesMap: React.FC<{ cities: string[]; active: string | null; label: (city: string) => string }> = ({
  cities,
  active,
  label,
}) => {
  const dots = useMemo(() => {
    const poly = BORDER.map(([lon, lat]) => [x(lon), y(lat)] as [number, number]);
    const out: [number, number][] = [];
    for (let gx = 0; gx < W; gx += STEP) for (let gy = 0; gy < H; gy += STEP) if (inside(gx, gy, poly)) out.push([gx, gy]);
    return out;
  }, []);

  const hq = BRANCH_COORDS[HQ_CITY];
  const hx = x(hq[0]);
  const hy = y(hq[1]);
  const pins = cities.filter((c) => BRANCH_COORDS[c]);
  // The hovered pin is drawn last so its label sits on top.
  const ordered = active ? [...pins.filter((c) => c !== active), active] : pins;

  return (
    <svg
      className={styles.mapSvg}
      viewBox={`-14 -14 ${Math.round(W + 28)} ${Math.round(H + 28)}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <g>
        {dots.map(([dx, dy]) => (
          <circle key={`${dx}-${dy}`} className={styles.mapDot} cx={dx} cy={dy} r={3.1} />
        ))}
      </g>
      <g>
        {pins
          .filter((c) => c !== HQ_CITY)
          .map((c) => {
            const [lon, lat] = BRANCH_COORDS[c];
            const px = x(lon);
            const py = y(lat);
            const mx = (hx + px) / 2;
            const my = (hy + py) / 2 - Math.hypot(px - hx, py - hy) * 0.22;
            return <path key={c} className={styles.mapArc} d={`M${hx} ${hy} Q${mx.toFixed(1)} ${my.toFixed(1)} ${px} ${py}`} />;
          })}
      </g>
      <text className={styles.seaLabel} x={x(36.4)} y={y(20.4)}>
        RED SEA
      </text>
      <text className={styles.seaLabel} x={x(49.6)} y={y(28.6)}>
        ARABIAN GULF
      </text>
      {ordered.map((c) => {
        const [lon, lat] = BRANCH_COORDS[c];
        const px = x(lon);
        const py = y(lat);
        const isHq = c === HQ_CITY;
        const on = c === active;
        const cls = [styles.pin, isHq ? styles.pinHq : '', on ? styles.pinOn : ''].join(' ');
        return (
          <g key={c} className={cls}>
            <circle className={styles.pinRing} cx={px} cy={py} r={isHq ? 13 : 9} />
            <circle className={styles.pinDot} cx={px} cy={py} r={isHq ? 9 : on ? 8 : 6} />
            {(isHq || on) && <Tag x={px} y={py} hq={isHq} text={isHq ? `${label(c)} · HQ` : label(c)} />}
          </g>
        );
      })}
    </svg>
  );
};

export default BranchesMap;
